/**
 * @name HomePageProdutosFavoritos
 * @description Componente para exibir produtos mais consumidos
 * @author Gabriel M. Guimarães | gabrielmg7
 */

import { Restaurant as RestaurantIcon, Star as StarIcon } from '@mui/icons-material';
import { Avatar, Box, Card, CardContent, Chip, Typography, useTheme } from '@mui/material';
import React from 'react';

interface IProdutosFavoritos {
     produto_id: number;
     nome_produto: string;
     total_consumido: number;
     frequencia: number;
}

interface HomePageProdutosFavoritosProps {
     data: IProdutosFavoritos[];
     title: string;
}

export const HomePageProdutosFavoritos: React.FC<HomePageProdutosFavoritosProps> = ({ data, title }) => {
     const theme = useTheme();

     const getProdutoIcon = (nome: string) => {
          const nomeNormalizado = nome.toLowerCase();

          if (nomeNormalizado.includes('suco') || nomeNormalizado.includes('água')) {
               return '🥤';
          } else if (nomeNormalizado.includes('lanche') || nomeNormalizado.includes('pão')) {
               return '🥪';
          } else if (nomeNormalizado.includes('doce') || nomeNormalizado.includes('bolo')) {
               return '🍰';
          } else if (nomeNormalizado.includes('fruta') || nomeNormalizado.includes('maçã')) {
               return '🍎';
          } else if (nomeNormalizado.includes('biscoito') || nomeNormalizado.includes('cookie')) {
               return '🍪';
          } else {
               return '🍽️';
          }
     };

     const getRankColor = (index: number) => {
          switch (index) {
               case 0:
                    return theme.palette.warning.main; // Ouro
               case 1:
                    return theme.palette.grey[400]; // Prata
               case 2:
                    return '#cd7f32'; // Bronze
               default:
                    return theme.palette.primary.main;
          }
     };

     const getRankIcon = (index: number) => {
          switch (index) {
               case 0:
                    return '🥇';
               case 1:
                    return '🥈';
               case 2:
                    return '🥉';
               default:
                    return `${index + 1}º`;
          }
     };

     return (
          <Card sx={{ height: '100%' }}>
               <CardContent sx={{ p: 3 }}>
                    <Typography
                         variant='h6'
                         gutterBottom
                         fontWeight='bold'
                    >
                         {title}
                    </Typography>

                    {data.length === 0 ? (
                         <Box
                              sx={{
                                   height: 200,
                                   display: 'flex',
                                   flexDirection: 'column',
                                   alignItems: 'center',
                                   justifyContent: 'center',
                                   gap: 2,
                              }}
                         >
                              <RestaurantIcon sx={{ fontSize: 48, color: theme.palette.text.secondary }} />
                              <Typography
                                   variant='body2'
                                   color='text.secondary'
                                   textAlign='center'
                              >
                                   Nenhum produto consumido ainda
                              </Typography>
                         </Box>
                    ) : (
                         <Box>
                              {data.slice(0, 5).map((produto, index) => (
                                   <Box
                                        key={produto.produto_id}
                                        sx={{
                                             display: 'flex',
                                             alignItems: 'center',
                                             gap: 2,
                                             mb: 2,
                                             p: 2,
                                             backgroundColor: index < 3 ? `${getRankColor(index)}10` : theme.palette.background.default,
                                             border: index < 3 ? `1px solid ${getRankColor(index)}40` : `1px solid ${theme.palette.divider}`,
                                             borderRadius: 2,
                                             transition: 'transform 0.2s ease',
                                             '&:hover': {
                                                  transform: 'translateY(-2px)',
                                             },
                                        }}
                                   >
                                        {/* Ranking */}
                                        <Avatar
                                             sx={{
                                                  width: 32,
                                                  height: 32,
                                                  backgroundColor: getRankColor(index),
                                                  color: 'white',
                                                  fontSize: '0.8rem',
                                                  fontWeight: 'bold',
                                             }}
                                        >
                                             {typeof getRankIcon(index) === 'string' && getRankIcon(index).includes('º') ? getRankIcon(index) : getRankIcon(index)}
                                        </Avatar>

                                        {/* Ícone do produto */}
                                        <Box sx={{ fontSize: '1.5rem' }}>{getProdutoIcon(produto.nome_produto)}</Box>

                                        {/* Informações do produto */}
                                        <Box sx={{ flex: 1 }}>
                                             <Typography
                                                  variant='subtitle2'
                                                  fontWeight='bold'
                                                  sx={{
                                                       mb: 0.5,
                                                       color: index < 3 ? getRankColor(index) : theme.palette.text.primary,
                                                  }}
                                             >
                                                  {produto.nome_produto}
                                             </Typography>
                                             <Typography
                                                  variant='caption'
                                                  color='text.secondary'
                                             >
                                                  {produto.total_consumido} unidades consumidas
                                             </Typography>
                                        </Box>

                                        {/* Frequência */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                             {index === 0 && <StarIcon sx={{ color: theme.palette.warning.main, fontSize: 16 }} />}
                                             <Chip
                                                  label={`${produto.frequencia}x`}
                                                  size='small'
                                                  sx={{
                                                       backgroundColor: getRankColor(index),
                                                       color: 'white',
                                                       fontWeight: 'bold',
                                                       fontSize: '0.75rem',
                                                  }}
                                             />
                                        </Box>
                                   </Box>
                              ))}

                              {data.length > 5 && (
                                   <Box sx={{ mt: 2, textAlign: 'center' }}>
                                        <Typography
                                             variant='caption'
                                             color='text.secondary'
                                        >
                                             +{data.length - 5} outros produtos
                                        </Typography>
                                   </Box>
                              )}
                         </Box>
                    )}
               </CardContent>
          </Card>
     );
};
